import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';

import { useSessionStorage } from 'react-use';
import styled, { createGlobalStyle } from 'styled-components';
import { notification, Modal } from 'antd';
import { useSelector } from 'react-redux';
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import { ProviderAPI, useGetApi } from 'providerSite/api';
import RichTextField from './input/RichTextField';
import style from 'style';
import * as track from 'lib/track';


const Container = styled.div`
  position: relative;
`;

const ModalStyles = createGlobalStyle`
  .custom-announcement-modal .ant-modal-content {
    border-radius: 38px;
    overflow: hidden;
  }
`;

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${style.zIndexes.banner};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  background: ${({bgColor}) => bgColor};
  border-top: 3px solid ${({borderColor}) => borderColor};
  backdrop-filter: blur(8px);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 25%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 75%,
      transparent 100%
    );
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 200%;
    }
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BannerDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  flex-shrink: 0;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
    }
    50% {
      opacity: 0.6;
      box-shadow: 0 0 0 6px rgba(255, 255, 255, 0);
    }
  }
`;

const BannerText = styled.div`
  color: ${({textColor}) => textColor};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;

  p {
    margin: 0;
  }
`;

const ModalIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({color}) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: bounceIn 0.6s ease-out, pulse 2s ease-in-out 0.6s infinite;

  @keyframes bounceIn {
    0% {
      opacity: 0;
      transform: scale(0.3);
    }
    50% {
      transform: scale(1.1);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    50% {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25), 0 0 0 8px rgba(0, 0, 0, 0.05);
    }
  }
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  animation: fadeInUp 0.4s ease-out 0.2s both;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalContent = styled.div`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
  animation: fadeInUp 0.4s ease-out 0.3s both;
`;


const Announcement = ({ children }) => {
  const { data = [], refetch } = useGetApi(ProviderAPI.GET_ANNOUNCEMENTS, {
    staleTimeMinute: 5,
  });
  const [announcementIds, setAnnouncementIds] = useSessionStorage('announcementIds', []);
  const shownIdsRef = useRef(new Set());
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const profile = useSelector(state => state.auth.profile);

  const addId = (uuid) => {
    setAnnouncementIds(prev => {
      if (prev.includes(uuid)) {
        return prev;
      }
      return [...prev, uuid];
    });
  };

  useEffect(() => {
    refetch();
  }, [isAuthenticated]);

  const getNotificationConfig = (severity) => {
    switch (severity) {
    case 'warning':
      return {
        color: style.colors.warning,
        icon: <WarningOutlined style={{ color: style.colors.warning }} />,
      };
    case 'error':
      return {
        color: style.colors.error,
        icon: <CloseCircleOutlined style={{ color: style.colors.error }} />,
      };
    case 'success':
      return {
        color: style.colors.success,
        icon: <CheckCircleOutlined style={{ color: style.colors.success }} />,
      };
    default:
      return {
        color: style.colors.info,
        icon: <InfoCircleOutlined style={{ color: style.colors.info }} />,
      };
    }
  };

  const openNotification = (data) => {
    const config = getNotificationConfig(data.severity);
    track.sendEvent('announcement-notification', 'view', { uuid: data.uuid }, [profile?.uuid]);
    notification.open({
      message: <span style={{ fontWeight: 600, color: '#1f2937' }}>{data.title || 'Notification'}</span>,
      placement: 'bottomLeft',
      duration: data.duration,
      onClose: () => addId(data.uuid),
      icon: config.icon,
      style: {
        borderLeft: `4px solid ${config.color}`,
        borderRadius: '16px',
      },
      description: (
        <div style={{ color: '#6b7280', fontSize: '14px' }}>
          <RichTextField inputValue={data.data} />
        </div>
      ),
    });
  };

  const getModalConfig = (severity) => {
    switch (severity) {
    case 'warning':
      return {
        color: style.colors.warning,
        icon: <WarningOutlined style={{ fontSize: 28, color: 'white' }} />,
      };
    case 'error':
      return {
        color: style.colors.error,
        icon: <CloseCircleOutlined style={{ fontSize: 28, color: 'white' }} />,
      };
    case 'success':
      return {
        color: style.colors.success,
        icon: <CheckCircleOutlined style={{ fontSize: 28, color: 'white' }} />,
      };
    default:
      return {
        color: style.colors.info,
        icon: <InfoCircleOutlined style={{ fontSize: 28, color: 'white' }} />,
      };
    }
  };

  const openModal = (data) => {
    const config = getModalConfig(data.severity);
    track.sendEvent('announcement-modal', 'view', { uuid: data.uuid }, [profile?.uuid]);
    Modal.info({
      title: null,
      icon: null,
      centered: true,
      maskStyle: { backgroundColor: 'rgba(0, 0, 0, 0.25)' },
      className: 'custom-announcement-modal',
      onCancel: () => addId(data.uuid),
      onOk: () => addId(data.uuid),
      okButtonProps: {
        style: {
          background: config.color,
          border: 'none',
          borderRadius: '6px',
          fontWeight: 500,
        }
      },
      content: (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <ModalIconWrapper color={config.color}>
            {config.icon}
          </ModalIconWrapper>
          <ModalTitle>
            {data.title || 'Announcement'}
          </ModalTitle>
          <ModalContent>
            <RichTextField inputValue={data.data} />
          </ModalContent>
        </div>
      ),
    });
  };

  const banner = data.find(item => item.announcementType === 'banner');

  const getBannerConfig = (severity) => {
    switch (severity) {
    case 'warning':
      return {
        bgColor: 'linear-gradient(90deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 50%, rgba(180, 83, 9, 0.9) 100%)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        textColor: '#ffffff',
      };
    case 'error':
      return {
        bgColor: 'linear-gradient(90deg, rgba(190, 24, 93, 0.9) 0%, rgba(219, 39, 119, 0.9) 50%, rgba(225, 29, 72, 0.9) 100%)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        textColor: '#ffffff',
      };
    case 'success':
      return {
        bgColor: 'linear-gradient(90deg, rgba(35, 120, 4, 0.9) 0%, rgba(56, 158, 13, 0.9) 50%, rgba(82, 196, 26, 0.9) 100%)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        textColor: '#ffffff',
      };
    default:
      return {
        bgColor: 'linear-gradient(90deg, rgba(9, 88, 217, 0.9) 0%, rgba(9, 109, 217, 0.9) 50%, rgba(24, 144, 255, 0.9) 100%)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        textColor: '#ffffff',
      };
    }
  };

  const bannerConfig = banner ? getBannerConfig(banner.severity) : null;

  useEffect(() => {
    for (const item of data) {
      // Skip if already dismissed (in session storage) or already shown in this session
      if (announcementIds.includes(item.uuid) || shownIdsRef.current.has(item.uuid)) {
        continue;
      }
      // Mark as shown in this session
      shownIdsRef.current.add(item.uuid);

      if (item.announcementType === 'notification') {
        openNotification(item);
      } else if (item.announcementType === 'modal') {
        openModal(item);
      }
    }
  }, [data]);

  return (
    <Container>
      <ModalStyles />
      {banner && bannerConfig && (
        <Banner bgColor={bannerConfig.bgColor} borderColor={bannerConfig.borderColor}>
          <BannerContent>
            <BannerDot />
            <BannerText textColor={bannerConfig.textColor}>
              <RichTextField inputValue={banner.data} />
            </BannerText>
          </BannerContent>
        </Banner>
      )}
      {children}
    </Container>
  );
};

Announcement.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Announcement;
